import React from 'react';
import ListItemButton from '@mui/material/ListItemButton';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { Typography, Pagination, Button } from "@mui/material";
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import {getInventoryByBatchCode} from "../../utilityFunctions/WarehouseUtil";
import { PuffLoader } from "react-spinners";
import { css } from "@emotion/react";
import Colors from "../../components/ui/Colors";
import { format } from 'date-fns';
import { useHistory } from 'react-router-dom';

const cssLoader = css`
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

export default function InventoryByCode(props) {

    const {loginUser} = props;
    const history = useHistory();
    const location = history.location;
    const product = location.state?.product;
    const itemNumber = product?.itemNumber;
    const productDescription = product?.description;

    const [loading, setLoading] = React.useState(false);
    const [page, setPage] = React.useState(0);
    const [totalPages, setTotalPages] = React.useState(1);
    const pageSize = 10;
    const [title, setTitle] = React.useState("Inventario por Lote");

    React.useEffect(() => {
        if (product) {
            setTitle(`Inventario por Lote - ${productDescription}`);
        }
        setLoading(true);
        renderInventory();
    }, [page, itemNumber]);

    const [inventory, setInventory] = React.useState("");

    const renderInventory = async() => {
        setLoading(true);
        
        const requestObject = {
            page: page,
            size: pageSize
        };
        
        // Add itemNumber to the request if available
        if (itemNumber) {
            requestObject.itemNumber = itemNumber;
        }
        
        const inventoryData = await getInventoryByBatchCode(requestObject, loginUser.jwtToken);
        setLoading(false);

        if(!inventoryData){
            return;
        }

        // Estimate total pages based on data size
        if (inventoryData.length === pageSize) {
            // If we got a full page, there might be more
            setTotalPages(page + 2); // Current page (0-based) + 1 for current + 1 for potential next
        } else if (inventoryData.length > 0 && page > 0) {
            // If we got partial data and we're not on page 0
            setTotalPages(page + 1);
        } else if (inventoryData.length > 0) {
            // If we're on page 0 with data
            setTotalPages(1);
        } else {
            // No data
            setTotalPages(0);
        }

        setInventory(
            <List sx={{width: "100%", marginTop: {xs: "3rem", sm: "2rem", md: "2rm"}}}>
                {inventoryData.map((item) => {
                    // Format the expiration date
                    const expirationDate = new Date(item.expirationDate);
                    const formattedDate = format(expirationDate, 'dd/MM/yyyy');
                    
                    return(
                        <ListItem key={generateRandomKey()} disablePadding divider sx={{marginBottom: "1.5rem"}}>
                            <ListItemButton key={generateRandomKey()}>
                                <Grid key={generateRandomKey()}  container spacing={1}>
                                    <Grid key={generateRandomKey()} item xs={3} sm={3} md={3} sx={{display: "flex", justifyContent: "center"}}>
                                        <Typography key={generateRandomKey()} variant="body2">{item.batchCode}</Typography>
                                    </Grid>
                                    <Grid key={generateRandomKey()} item xs={3} sm={3} md={3} sx={{display: "flex", justifyContent: "center"}}>
                                        <Typography key={generateRandomKey()} variant="body2">{new Intl.NumberFormat('es-CO').format(item.totalAmount)}</Typography>
                                    </Grid>
                                    <Grid key={generateRandomKey()} item xs={3} sm={3} md={3} sx={{display: "flex", justifyContent: "center"}}>
                                        <Typography key={generateRandomKey()} variant="body2">{formattedDate}</Typography>
                                    </Grid>
                                    <Grid key={generateRandomKey()} item xs={3} sm={3} md={3} sx={{display: "flex", justifyContent: "center"}}>
                                        <Typography 
                                            key={generateRandomKey()} 
                                            variant="body2" 
                                            sx={{
                                                color: item.daysUntilExpiration < 30 ? 'red' : 
                                                      item.daysUntilExpiration < 60 ? 'orange' : 'green'
                                            }}
                                        >
                                            {item.daysUntilExpiration}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </ListItemButton>
                        </ListItem>
                    )
                })}
            </List>
        );
    };

    const handlePageChange = (event, value) => {
        setPage(value - 1); // Pagination component is 1-based, our API is 0-based
    };

    const generateRandomKey = () => {
        const randomString = Math.random().toString(36).slice(2);
        const timestamp = Date.now().toString(36);
        return randomString + '_' + timestamp;
    };

    return (
        <Box sx={{display: "flex", justifyContent: "center", width: "100%", height: "100%"}}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: {xs: "start", sm: "center"},
                    width: "90%",
                    height: "100%",
                    textAlign: "center",
                    overflowY: "scroll",
                }}
            >
            {loading ? <PuffLoader size={100} color={Colors.secondary} css={cssLoader} /> :
            <Grid container>
                <Grid item xs={12} sm={12} md={12} sx={{ display: "flex", justifyContent: "space-between", mb: 2, alignItems: "center" }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {title}
                    </Typography>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={() => history.push("/warehouse/inventory")}
                    >
                        Ver Inventario por Producto
                    </Button>
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                    <Grid container sx={{marginTop: "1rem"}}>
                        <Grid key={generateRandomKey()} item xs={3} sm={3} md={3} sx={{display: "flex", justifyContent: "center"}}>
                            <Typography variant="body1" sx={{color: "black", fontWeight: "bold", textDecoration: 'underline',}}>Lote</Typography>
                        </Grid>
                        <Grid key={generateRandomKey()} item xs={3} sm={3} md={3} sx={{display: "flex", justifyContent: "center"}}>
                            <Typography variant="body1" sx={{color: "black", fontWeight: "bold", textDecoration: 'underline',}}>Cantidad</Typography>
                        </Grid>
                        <Grid key={generateRandomKey()} item xs={3} sm={3} md={3} sx={{display: "flex", justifyContent: "center"}}>
                            <Typography variant="body1" sx={{color: "black", fontWeight: "bold", textDecoration: 'underline',}}>Fecha Vencimiento</Typography>
                        </Grid>
                        <Grid key={generateRandomKey()} item xs={3} sm={3} md={3} sx={{display: "flex", justifyContent: "center"}}>
                            <Typography variant="body1" sx={{color: "black", fontWeight: "bold", textDecoration: 'underline',}}>Días por Vencer</Typography>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                    {inventory}
                </Grid>
                <Grid item xs={12} sm={12} md={12} sx={{display: "flex", justifyContent: "center", mt: 2}}>
                    <Pagination 
                        count={totalPages} 
                        page={page + 1} 
                        onChange={handlePageChange} 
                        color="primary" 
                    />
                </Grid>
            </Grid>
            }
            </Box>
        </Box>
    )
}
